import { CaseReviewEnum, type DisplayableCaseReview } from '@/citator/schema/case-review-schema'
import type { ReviewDotColor } from '@/citator/components/ReviewDot'

export type CaseReviewInfo = {
  /** Short label rendered in the chip and as the popover header. */
  label: string
  /** Dot color in the chip; encodes the treatment category (red/yellow/green). */
  dotColor: ReviewDotColor
  /** Long-form explanation rendered in the popover body. */
  body: string
}

/**
 * Per-enum label, dot color, and tooltip body for citing-case review tags. Positive
 * reviews are deferred to a follow-on PR; re-add the `affirmed` row when reintroducing.
 */
export const CASE_REVIEW_INFO: Record<DisplayableCaseReview, CaseReviewInfo> = {
  // hard-negative (red)
  [CaseReviewEnum.overturned]: {
    label: 'Overturning',
    dotColor: 'red',
    body: 'This court is overturning the holding in the cited case.',
  },
  [CaseReviewEnum.overturned_in_part]: {
    label: 'Overturning in part',
    dotColor: 'red',
    body: "This court is overturning some, but not all, of the cited case's holdings.",
  },
  [CaseReviewEnum.vacated]: {
    label: 'Vacating',
    dotColor: 'red',
    body: 'This court is setting aside the judgment in the cited case.',
  },
  [CaseReviewEnum.overturning_recognized]: {
    label: 'Recognizing as overturned',
    dotColor: 'red',
    body: 'This court is acknowledging that this case has been overturned, without itself being the overturning authority.',
  },

  // soft-negative (yellow)
  [CaseReviewEnum.questioned]: {
    label: 'Questioning',
    dotColor: 'yellow',
    body: "This court is questioning the cited case's reasoning or continued validity.",
  },
  [CaseReviewEnum.refined]: {
    label: 'Refining',
    dotColor: 'yellow',
    body: "This court narrowed the application of the cited case's holding without overturning it.",
  },
  [CaseReviewEnum.distinguished]: {
    label: 'Distinguishing',
    dotColor: 'yellow',
    body: "This court declined to apply the cited case's holding, finding the facts of this case materially different.",
  },
}
